import { hasAnyTranslation, translateBackend } from '@assets/i18n/config';
import type { Account } from '@interfaces/account';
import type { TranslatedString } from '@interfaces/shared';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import type { AccountGroupType } from '@ovComponents/4-module/widgets/editButton/pageTypes/components/editAccountGroupDialog';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { Box, CircularProgress, Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { AccountGroupSection } from './components/accountGroupSection';
import { DraggableUngroupedAccounts } from './components/draggableUngroupedAccounts';
import type { AccountGroupWorkflowOptions } from './createAccountGroup';
import { handleAccountGroupDragEnd } from './createAccountGroup.utils';

type Props = {
  options: AccountGroupWorkflowOptions;
  onContinue: (updatedAccountGroup: {
    translatedName: TranslatedString;
    accountIds: string[];
  }) => void;
  loading?: boolean;
  dummyAccounts?: Account[];
  objectType?: 'USER' | 'HOUSEHOLD' | 'ORGANIZATION';
  objectId?: string;
};

export const CreateAccountGroupVisual = ({
  options,
  onContinue,
  loading,
  dummyAccounts,
  objectType,
  objectId,
}: Props) => {
  const { t } = useTranslation('client');
  const [accountGroup, setAccountGroup] = useState<AccountGroupType>({
    id: `accountGroup-${Date.now()}`,
    translatedName: getLanguageDefaults(),
    accounts: [],
  });
  const [ungroupedAccounts, setUngroupedAccounts] = useState<Account[]>([]);

  useEffect(() => {
    setUngroupedAccounts(dummyAccounts || []);
  }, [dummyAccounts]);

  const removeAccountGroup = () => {
    setUngroupedAccounts([...ungroupedAccounts, ...accountGroup.accounts]);
    setAccountGroup({
      id: `accountGroup-${Date.now()}`,
      translatedName: getLanguageDefaults(),
      accounts: [],
    });
  };

  const updateAccountGroup = (
    key: string,
    value: TranslatedString | Account[],
  ) => {
    setAccountGroup({ ...accountGroup, [key]: value });
  };

  const handleRemoveAccount = (accountId: string, account: Account) => {
    updateAccountGroup(
      'accounts',
      accountGroup.accounts.filter((a) => a.id !== accountId),
    );
    setUngroupedAccounts([...ungroupedAccounts, account]);
  };

  const onDragEnd = handleAccountGroupDragEnd({
    variant: 'create',
    accountGroup,
    setAccountGroup,
    ungroupedAccounts,
    setUngroupedAccounts,
    updateAccountGroup,
    disableAccountsReordering: true,
  });

  const disabled =
    (dummyAccounts?.length ?? 0) > 0 ||
    accountGroup.accounts.length === 0 ||
    !hasAnyTranslation(accountGroup.translatedName);

  const submit = () =>
    onContinue({
      translatedName: accountGroup.translatedName,
      accountIds: accountGroup.accounts.map((a) => a.id),
    });

  if (loading) {
    return (
      <Box
        sx={{
          height: '60vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='create-account-group-for-account-form'
      buttonFooter={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='bodyMedium'
            color='textSecondary'
            sx={{ mt: '20px', fontWeight: 500 }}
          >
            {t('editAccountGroup.greyedAccountsCopy')}
          </Typography>
        </Box>
      }
      submitButtonProps={{
        label: t('continue'),
        disabled: loading || disabled,
        dataTestId: `create-account-group-for-account-${kebabCase(translateBackend(options?.title))}-continue-button`,
      }}
    >
      <Typography variant='displayLarge' sx={{ mb: 2 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 2 }}>
        {translateBackend(options?.subTitle)}
      </Typography>
      {options?.accountGroupSection?.enabled &&
        options?.ungroupedAccounts?.enabled && (
          <DragDropContext onDragEnd={onDragEnd}>
            <AccountGroupSection
              objectId={objectId}
              objectType={objectType}
              accountGroup={accountGroup}
              setAccountGroup={setAccountGroup}
              variant='create'
              updateAccountGroup={updateAccountGroup}
              removeAccount={handleRemoveAccount}
              removeAccountGroup={removeAccountGroup}
              showAccountRemoveIcon
              showGroupRemoveIcon
            />
            <DraggableUngroupedAccounts
              objectType={objectType}
              objectId={objectId}
              ungroupedAccounts={ungroupedAccounts}
              setUngroupedAccounts={setUngroupedAccounts}
              variant='create'
              excludeAccountIds={accountGroup.accounts.map((a) => a.id)}
            />
          </DragDropContext>
        )}
    </WorkflowCompletionForm>
  );
};
